import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Award,
  Calendar,
  Globe,
  Mail,
  ExternalLink,
  Maximize2,
  Shield,
  UserPlus,
  X,
} from 'lucide-react';
import GalleryLightbox from '@/components/GalleryLightbox';
import { LinkPreviewCard } from '@/components/LinkPreviewCard';

const EntityGallery = ({ images }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const previewCount = 4;
  const visible = images.slice(0, previewCount);
  const remaining = images.length - visible.length;

  const open = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-card rounded-2xl border border-border/50 p-6" data-testid="entity-gallery">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Gallery</h2>
        <button
          type="button"
          onClick={() => open(0)}
          className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
          data-testid="entity-gallery-view-all"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          View gallery ({images.length})
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {visible.map((img, idx) => {
          const isLastWithOverlay = idx === visible.length - 1 && remaining > 0;
          return (
            <button
              type="button"
              key={img || `gallery-${idx}`}
              onClick={() => open(idx)}
              className="relative group rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid={`entity-gallery-thumb-${idx}`}
            >
              <img
                src={img}
                alt={`Gallery ${idx + 1}`}
                className="w-full h-32 object-cover transition-transform group-hover:scale-105"
              />
              {isLastWithOverlay && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-medium">
                  +{remaining} more
                </div>
              )}
            </button>
          );
        })}
      </div>

      <GalleryLightbox
        images={images}
        open={lightboxOpen}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
};

const EntityAdminControls = ({
  entityAdminUsers,
  entityAdminEmails,
  setEntityAdminEmails,
  onSaveEntityAdmins,
  onRemoveEntityAdmin,
}) => {
  return (
    <div className="bg-card rounded-2xl border border-primary/20 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Partner Admins</h2>
        <Badge variant="outline" className="text-xs">Super Admin Only</Badge>
      </div>

      {entityAdminUsers.length > 0 && (
        <div className="mb-4 space-y-2">
          <Label className="text-sm font-medium">Current Partner Admins ({entityAdminUsers.length}/2)</Label>
          {entityAdminUsers.map((admin) => (
            <div key={admin.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                {admin.picture && (
                  <img src={admin.picture} alt={admin.name} className="w-8 h-8 rounded-full" />
                )}
                <div>
                  <p className="font-medium text-sm">{admin.name}</p>
                  <p className="text-xs text-muted-foreground">{admin.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveEntityAdmin(admin.user_id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <Label className="text-sm font-medium">Add Partner Admins by Email</Label>
        {/* Positional slots (max 2). Slots never reorder — they fill
            position-by-position — so a slot-keyed identifier is the
            right choice (not user input that could shift). */}
        {entityAdminEmails.map((email, idx) => (
          <div key={`entity-admin-slot-${idx}`} className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            <Input
              type="email"
              placeholder={`Partner Admin ${idx + 1} Email`}
              value={email}
              onChange={(e) => {
                const newEmails = [...entityAdminEmails];
                newEmails[idx] = e.target.value;
                setEntityAdminEmails(newEmails);
              }}
              className="flex-1"
            />
          </div>
        ))}
        <Button onClick={onSaveEntityAdmins} className="w-full" size="sm">
          Save Entity Admins
        </Button>
        <p className="text-xs text-muted-foreground">
          Entity admins can manage this entity's profile, gallery, promo codes, and offers from their dashboard. Maximum 2 admins allowed.
        </p>
      </div>
    </div>
  );
};

const EntitySidebar = ({
  entity,
  user,
  entityAdminUsers,
  entityAdminEmails,
  setEntityAdminEmails,
  onSaveEntityAdmins,
  onRemoveEntityAdmin,
}) => {
  return (
    <div className="space-y-6">
      {/* Image Gallery */}
      <EntityGallery images={entity.image_gallery} />

      {/* Promotional Codes */}
      {entity.promo_codes && entity.promo_codes.length > 0 && (
        <div className="bg-card rounded-2xl border border-border/50 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Promo Codes
          </h2>
          <div className="space-y-3">
            {entity.promo_codes.map((promo, idx) => (
              <div key={promo.code || `promo-${idx}`} className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
                <div className="flex items-start justify-between mb-1">
                  <div className="font-mono font-bold text-primary">{promo.code}</div>
                  <Badge variant="secondary" className="text-xs">{promo.discount}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-1">{promo.description}</p>
                <div className="flex items-center justify-between">
                  {promo.expiry_date && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Until {new Date(promo.expiry_date).toLocaleDateString()}
                    </p>
                  )}
                  {promo.affiliate_link && (
                    <a href={promo.affiliate_link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                      Redeem <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Special Offers */}
      {entity.offers && entity.offers.length > 0 && (
        <div className="bg-card rounded-2xl border border-border/50 p-6">
          <h2 className="text-lg font-semibold mb-4">Special Offers</h2>
          <div className="space-y-3">
            {entity.offers.map((offer, idx) => (
              <div key={offer.title ? `${offer.title}-${idx}` : `offer-${idx}`} className="p-4 rounded-xl bg-muted/50">
                <h3 className="font-semibold mb-1">{offer.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">{offer.description}</p>
                <div className="flex items-center justify-between">
                  {offer.valid_until && (
                    <div className="flex items-center gap-1 text-xs text-primary">
                      <Calendar className="h-3 w-3" />
                      Until {new Date(offer.valid_until).toLocaleDateString()}
                    </div>
                  )}
                  {offer.affiliate_link && (
                    <a href={offer.affiliate_link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                      Learn More <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entity Admin Management - Super Admin Only */}
      {user?.admin_role === 'super_admin' && (
        <EntityAdminControls
          entityAdminUsers={entityAdminUsers}
          entityAdminEmails={entityAdminEmails}
          setEntityAdminEmails={setEntityAdminEmails}
          onSaveEntityAdmins={onSaveEntityAdmins}
          onRemoveEntityAdmin={onRemoveEntityAdmin}
        />
      )}

      {/* Quick Info */}
      <div className="bg-card rounded-2xl border border-border/50 p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Info</h2>
        <div className="space-y-4">
          {entity.years_experience > 0 && (
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <span>{entity.years_experience} years experience</span>
            </div>
          )}
          {entity.website && (
            <a
              href={entity.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-primary hover:underline"
            >
              <Globe className="h-5 w-5" />
              <span>Visit Website</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {entity.contact_email && (
            <a
              href={`mailto:${entity.contact_email}`}
              className="flex items-center gap-3 text-primary hover:underline"
            >
              <Mail className="h-5 w-5" />
              <span>Send Email</span>
            </a>
          )}
        </div>
      </div>

      {/* Website preview card — richer than a raw "Visit Website" link. Only
          renders on entities without their own gallery so we don't drown out
          uploaded media. */}
      {entity.website && (!entity.gallery_images || entity.gallery_images.length === 0) && (
        <LinkPreviewCard url={entity.website} />
      )}

      {/* Tags */}
      {entity.tags?.length > 0 && (
        <div className="bg-card rounded-2xl border border-border/50 p-6">
          <h2 className="text-lg font-semibold mb-4">Tags</h2>
          <div className="flex flex-wrap gap-2">
            {entity.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntitySidebar;
